import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ProfessionalCard } from "@/components/professionals/ProfessionalCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CarouselDots } from "@/components/ui/CarouselDots";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback } from "react";

interface Professional {
  id: string;
  business_name: string;
  slug: string;
  categories: string[];
  address_city: string;
  address_region?: string;
  profile_image: string;
  is_verified: boolean;
  first_name?: string;
  last_name?: string;
  average_rating?: number;
  review_count?: number;
  is_company?: boolean;
  subscription_type?: 'base' | 'pro' | 'plus' | null;
  languages_spoken?: string[];
  tagline_it?: string;
  tagline_en?: string;
  tagline_es?: string;
}

interface ProfessionalsCarouselProps {
  professionals: Professional[];
  isLoading?: boolean;
}

// Skeleton card for loading state
const ProfessionalCardSkeleton = () => (
  <div className="border-[3px] border-dark bg-card shadow-[6px_6px_0px_0px] shadow-dark overflow-hidden h-full flex flex-col">
    <Skeleton className="w-full aspect-square" />
    <div className="p-4 space-y-3">
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-2/3" />
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-4 w-4" />
        ))}
      </div>
      <Skeleton className="h-10 w-full mt-4" />
    </div>
  </div>
);

export const ProfessionalsCarousel = ({ professionals, isLoading = false }: ProfessionalsCarouselProps) => {
  const { t } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: true });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Show skeleton during loading
  if (isLoading) {
    return (
      <section className="py-16 px-4 bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <ProfessionalCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (professionals.length === 0) return null;

  return (
    <section className="pt-16 pb-6 px-4 bg-surface">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="font-heading text-3xl font-bold">
            {t("home.latestProfessionalsTitle")}
          </h2>
          <Link 
            to="/professionals" 
            className="text-foreground hover:underline flex items-center gap-2 font-heading font-bold"
          >
            {t("home.viewAllProfessionals")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Previous Button - External */}
          <Button
            variant="outline"
            size="icon"
            onClick={scrollPrev}
            aria-label={t("common.previous")}
            className="hidden md:flex h-10 w-10 flex-shrink-0 border-[3px] border-dark shadow-[4px_4px_0px_#101322] bg-background hover:bg-muted hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_#101322] transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          {/* Carousel */}
          <div className="flex-1 overflow-hidden" ref={emblaRef}>
            <div className="flex -ml-4 py-2 pr-2">
              {professionals.map((prof) => (
                <div key={prof.id} className="pl-4 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%]">
                  <ProfessionalCard data={prof} />
                </div>
              ))}
            </div>
          </div>

          {/* Next Button - External */}
          <Button
            variant="outline"
            size="icon"
            onClick={scrollNext}
            aria-label={t("common.next")}
            className="hidden md:flex h-10 w-10 flex-shrink-0 border-[3px] border-dark shadow-[4px_4px_0px_#101322] bg-background hover:bg-muted hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_#101322] transition-all"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Pagination Dots */}
        <CarouselDots emblaApi={emblaApi} />
      </div>
    </section>
  );
};
